import { Layout } from "@/components/layout";
import { SEOHead } from "@/components/seo-head";
import { BLOG_POSTS } from "@/lib/content";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { FileText } from "lucide-react";

export default function BlogIndexPage() {
  const hasPosts = BLOG_POSTS.length > 0;

  return (
    <Layout>
      <SEOHead
        title="Blog | ClauseArk"
        description="Notes from the ClauseArk team on contract review, redlining, and the rest of the lifecycle."
      />

      <section className="pt-24 pb-20 md:pt-32 md:pb-24 border-b border-border/50 bg-slate-50 dark:bg-navy/30">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <h1 className="text-display mb-6 text-navy dark:text-white">
              Blog
            </h1>
            <p className="text-body-lg text-slate-600 dark:text-slate-300">
              Notes from the ClauseArk team on contract review, redlining, and the rest of the lifecycle.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 md:py-32">
        <div className="container mx-auto px-4 md:px-6">
          {hasPosts ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {BLOG_POSTS.map((post, i) => (
                <motion.div
                  key={post.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <Link href={`/resources/blog/${post.slug}`}>
                    <article className="group flex flex-col h-full bg-card rounded-2xl border p-6 hover:shadow-md transition-shadow cursor-pointer">
                      <div className="flex items-center gap-2 mb-4">
                        <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">
                          {post.tag}
                        </span>
                        <span className="text-xs text-muted-foreground">•</span>
                        <span className="text-xs text-muted-foreground">{post.readMin} min read</span>
                      </div>
                      <h2 className="text-xl font-bold mb-3 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors line-clamp-2">
                        {post.title}
                      </h2>
                      <p className="text-sm text-muted-foreground line-clamp-3 mb-6 flex-1">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center gap-3 mt-auto">
                        <div className="h-8 w-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-xs font-bold text-slate-600 dark:text-slate-300">
                          {post.author.charAt(0)}
                        </div>
                        <div className="text-sm">
                          <div className="font-medium text-foreground">{post.author}</div>
                          <div className="text-xs text-muted-foreground">{post.date}</div>
                        </div>
                      </div>
                    </article>
                  </Link>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="max-w-xl mx-auto text-center py-12">
              <div className="h-14 w-14 mx-auto rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 mb-6">
                <FileText className="h-7 w-7" />
              </div>
              <h2 className="text-h3 text-navy dark:text-white mb-3">No posts yet</h2>
              <p className="text-body text-slate-600 dark:text-slate-400 mb-8">
                We're heads-down building the product. When we publish notes from the ClauseArk team, they'll show up here.
              </p>
              <Link href="/demo">
                <Button size="lg" className="bg-brand-gradient text-white border-0 no-default-hover-elevate">
                  Book a demo instead
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
